/**
 * **Measured by** — a lookup of the company's measurement types
 * (`builder-engine-and-consistency.md` §6.7 item 1, board cb-04 cell 4; phone
 * cb-22 cell 2).
 *
 * Not a unit: a *type*, the same list a property's Measurements table is made
 * of, so the quote can read the property's own number for it. The unit comes
 * with the type, and there is no "create a type" row — types are managed where
 * they are today. Props only; the service pop-out uses it now, and the package
 * settings card does from step 10.
 */
import { useState } from 'react'
import { usePhone } from '../../lib/use-narrow.js'
import { cn } from '../../lib/cn.js'
import { MenuSelect } from '../../components/menu-select.js'
import { PanelSheet, SheetButton } from '../../components/panel-sheet.js'
import { IconChevronRight } from '../../frame/icons.js'
import {
  CANCEL, MEASURE_SHEET_SUB, MEASURE_TYPES_FOOT_PHONE, MEASURED_BY_LABEL, MEASURED_BY_MENU_FOOT,
  MEASURED_BY_NONE, MEASURED_BY_NONE_SUB, USE_TYPE,
} from '../copy.js'

export interface MeasureType { id: string; name: string; unit: string }

export function MeasureField({
  id, value, onChange, types, serviceName, label = MEASURED_BY_LABEL,
}: {
  id?: string
  value: string | null
  onChange: (id: string | null) => void
  /** Active types only, in their own order. */
  types: MeasureType[]
  /** For the phone sheet's sub-line — "Mulch beds · pick a measurement type". */
  serviceName: string
  label?: string
}) {
  const phone = usePhone()
  const chosen = types.find((type) => type.id === value) ?? null

  if (phone) return <MeasureSheetField {...{ value, onChange, types, serviceName, label, chosen }} />

  return (
    <MenuSelect
      id={id}
      size="field"
      label={label}
      value={value}
      onChange={onChange}
      minWidth={330}
      rowHeight={38}
      tickLeft
      clearable
      clearLast
      placeholder={MEASURED_BY_NONE}
      clearLabel={MEASURED_BY_NONE}
      clearSub={MEASURED_BY_NONE_SUB}
      footNote={MEASURED_BY_MENU_FOOT}
      options={types.map((type) => ({ value: type.id, label: type.name, detail: type.unit }))}
      triggerSub={(option) => `· ${option.detail ?? ''}`}
    />
  )
}

/** cb-22 · 2: a row that opens a sheet of radios, with Cancel · Use Bed area. */
function MeasureSheetField({
  value, onChange, types, serviceName, label, chosen,
}: {
  value: string | null
  onChange: (id: string | null) => void
  types: MeasureType[]
  serviceName: string
  label: string
  chosen: MeasureType | null
}) {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<string | null>(value)
  const pickedType = types.find((type) => type.id === picked) ?? null

  const rows: { id: string | null; name: string; sub: string }[] = [
    ...types.map((type) => ({ id: type.id, name: type.name, sub: type.unit })),
    { id: null, name: MEASURED_BY_NONE, sub: MEASURED_BY_NONE_SUB },
  ]

  return (
    <>
      <button
        type="button"
        aria-label={label}
        onClick={() => { setPicked(value); setOpen(true) }}
        className="flex min-h-[52px] w-full items-center gap-2 rounded-field border border-line bg-surface px-3.5 py-2 text-left"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[11.5px] text-text-muted">{label}</span>
          <span className="truncate text-[14px] font-semibold text-text">
            {chosen?.name ?? MEASURED_BY_NONE}
            {chosen && <span className="font-normal text-text-faint"> · {chosen.unit}</span>}
          </span>
        </span>
        <span className="text-text-faint"><IconChevronRight size={16} /></span>
      </button>

      <PanelSheet
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        sub={MEASURE_SHEET_SUB(serviceName)}
        layer={70}
        footer={(
          <>
            <SheetButton variant="default" onClick={() => setOpen(false)}>{CANCEL}</SheetButton>
            <SheetButton onClick={() => { onChange(picked); setOpen(false) }}>
              {pickedType ? USE_TYPE(pickedType.name) : USE_TYPE(MEASURED_BY_NONE)}
            </SheetButton>
          </>
        )}
      >
        <div role="radiogroup" aria-label={label} className="flex flex-col">
          {rows.map((row) => {
            const on = row.id === picked
            return (
              <button
                key={row.id ?? 'none'}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPicked(row.id)}
                className={cn('flex min-h-[56px] items-center gap-3 border-b border-line-faint px-4 text-left',
                  on && 'bg-accent-soft/50')}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[14.5px] font-semibold text-text">{row.name}</b>
                  <span className="text-[12px] text-text-muted">{row.sub}</span>
                </span>
                <span aria-hidden className={cn('flex h-[22px] w-[22px] items-center justify-center rounded-full border-2',
                  on ? 'border-accent' : 'border-line-strong')}>
                  {on && <span className="h-2.5 w-2.5 rounded-full bg-accent" />}
                </span>
              </button>
            )
          })}
          <p className="m-0 px-4 py-3 text-[12px] leading-[1.4] text-text-faint">{MEASURE_TYPES_FOOT_PHONE}</p>
        </div>
      </PanelSheet>
    </>
  )
}
